import { useState } from "react";
import styled from "@emotion/styled";
import { Link } from "@tanstack/react-router";
import { Input, Radio, Switch } from "antd5";
import { Flex, Text } from "styles/utility-components";

import { generateSavedViewTarget } from "components/saved_searches/utils";
import Button from "components/ui/button/Button";
import EmptyState from "components/ui/empty_state/EmptyState";
import Modal from "components/ui/modal/Modal";
import toast from "components/ui/toast/Toast";
import CentredSpinner from "lib/core_components/CentredSpinner";
import { useDeleteAllLeadSubscriptionContents } from "lib/hooks/api/lead_subscriptions/useDeleteAllLeadSubscriptionContents";
import { useLeadSubscriptions } from "lib/hooks/api/lead_subscriptions/useLeadSubscriptions";
import { useUpdateLeadSubscriptionContent } from "lib/hooks/api/lead_subscriptions/useUpdateLeadSubscriptionContent";
import { useAssertCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { useUpdateCurrentUser } from "lib/hooks/api/users/useUpdateCurrentUser";
import UIcon from "lib/icons/untitled_ui/UIcon";
import { LeadSubscriptionContentFrequency } from "lib/types/graphQLEnums";
import { SavedViewDataTypeUnion } from "lib/types/savedViews";

import css from "./EmailNotifications.module.scss";

type Alert = {
  viewId: string;
  viewName: string;
  viewDescription?: string | null;
  dataType: SavedViewDataTypeUnion;
  alertFrequency: string;
  contentId: string;
};

export function EmailNotifications() {
  return (
    <Flex column gap={24}>
      <Flex column gap={8}>
        <Text h3>Email notifications</Text>
        <Text color="sysTextSecondary">Manage your email preferences and alerts.</Text>
      </Flex>
      <MentionsCard />
      <Flex column gap={8}>
        <Text h3>Alerts</Text>
        <Text color="sysTextSecondary">
          Your daily or weekly updates for new activity in your saved searches
        </Text>
      </Flex>
      {/* TODO: Alerts Card */}
      <CustomAlerts />
    </Flex>
  );
}

function MentionsCard() {
  const currentUser = useAssertCurrentUser();

  const { mutate: updateCurrentUser, isLoading } = useUpdateCurrentUser({
    onSuccess: () => toast.success("Mentions updated successfully!"),
    onError: () => toast.error("Failed to update mentions"),
  });
  return (
    <Card flexWrap="wrap" justifyContent="space-between" alignItems="center" gap={16}>
      <Flex column gap={8}>
        <Text bold>Mentions</Text>
        <Text>
          Get an email every time someone mentions you in a comment or assigns an opportunity to
          you.
        </Text>
      </Flex>
      <Switch
        aria-label="Mentions"
        loading={isLoading}
        checked={currentUser.sendAppActivityNotifications}
        onChange={(active) =>
          updateCurrentUser({
            request: { guid: currentUser.guid, sendAppActivityNotifications: active },
          })
        }
      />
    </Card>
  );
}

function CustomAlerts() {
  const [searchText, setSearchText] = useState("");
  const [deleteAllOpen, setDeleteAllOpen] = useState(false);

  const { mutate: deleteAllAlerts, isLoading: isDeletingAllAlerts } =
    useDeleteAllLeadSubscriptionContents({
      onSuccess: () => {
        setDeleteAllOpen(false);
        toast.success("Alerts deleted successfully!");
      },
      onError: () => toast.error("Failed to delete alerts"),
    });

  const { data: subscriptions, isLoading } = useLeadSubscriptions();

  const allPersonalAlerts =
    subscriptions?.flatMap(
      (subscription) =>
        subscription.views?.map((view) => ({
          viewId: view.id,
          viewName: view.name,
          viewDescription: view.description,
          dataType: view.dataType,
          alertFrequency: subscription.frequency,
          contentId: view.contentId,
        })) ?? [],
    ) ?? [];

  const filteredAlertsNew = allPersonalAlerts.filter((alert) =>
    alert?.viewName?.toLowerCase().includes(searchText.toLowerCase()),
  );

  return (
    <Flex column gap={16}>
      <Modal
        open={deleteAllOpen}
        onOpenChange={setDeleteAllOpen}
        showCloseButton={false}
        padding={24}
      >
        <DialogWrapper column gap={16}>
          <Flex column gap={8}>
            <Text h2>Delete all alerts</Text>
            <Text color="sysTextSecondary">
              Are you sure you want to delete all alerts? You will not receive any updates on new
              search results.
            </Text>
          </Flex>
          <Flex justifyContent="flex-end" gap={8}>
            <Button buttonType="secondary" onClick={() => setDeleteAllOpen(false)}>
              Cancel
            </Button>
            <Button
              buttonStyle="destructive"
              onClick={() => deleteAllAlerts({})}
              loading={isDeletingAllAlerts}
            >
              Delete
            </Button>
          </Flex>
        </DialogWrapper>
      </Modal>
      <Flex justifyContent="space-between" alignItems="center" gap={16} flexWrap="wrap">
        <Flex gap={16}>
          <Input
            placeholder="Search alerts"
            onChange={(e) => setSearchText(e.target.value)}
            value={searchText}
            className={css.searchInput}
            prefix={<UIcon icon="searchSm" color="blue600" />}
            allowClear
          />
          {/* TODO: DataType Filter */}
        </Flex>
        <Button
          buttonStyle="destructive"
          buttonType="tertiary"
          disabled={allPersonalAlerts.length === 0}
          icon="trash01"
          onClick={() => setDeleteAllOpen(true)}
        >
          Delete all alerts
        </Button>
      </Flex>
      <Flex column gap={12} alignItems="center">
        <AlertResultsContainer
          filteredAlerts={filteredAlertsNew}
          alertsPresent={allPersonalAlerts.length > 0}
          isLoading={isLoading}
        />
      </Flex>
    </Flex>
  );
}

type AlertResultsContainerProps = {
  filteredAlerts: Alert[];
  alertsPresent: boolean;
  isLoading: boolean;
};

function AlertResultsContainer({
  filteredAlerts,
  alertsPresent,
  isLoading,
}: AlertResultsContainerProps) {
  if (isLoading) {
    return <CentredSpinner />;
  }

  if (!alertsPresent) {
    return (
      <EmptyState
        size="large"
        type="notifications"
        heading="No alerts set"
        description="Create alerts on any saved searches"
      />
    );
  }

  return (
    <>
      {filteredAlerts.map((alert) => (
        <CustomAlert alert={alert} key={alert.viewId} />
      ))}
    </>
  );
}

type CustomAlertProps = {
  alert: Alert;
};

function CustomAlert({ alert }: CustomAlertProps) {
  const { viewId, viewName, dataType, alertFrequency, contentId } = alert;
  const [deleteAlertOpen, setDeleteAlertOpen] = useState(false);

  const { mutate: updateAlert, isLoading: isUpdatingAlert } = useUpdateLeadSubscriptionContent({
    onSuccess: (_, variables) => {
      setDeleteAlertOpen(false);
      toast.success(
        variables.input.frequency === LeadSubscriptionContentFrequency.None
          ? "Alert deleted successfully!"
          : "Alert updated successfully!",
      );
    },
    onError: (_, variables) =>
      toast.error(
        variables.input.frequency === LeadSubscriptionContentFrequency.None
          ? "Failed to delete alert"
          : "Failed to update alert",
      ),
  });

  return (
    <AlertCard width="100%" justifyContent="space-between" gap={8}>
      <div>
        <Link {...generateSavedViewTarget(viewId, dataType)}>{viewName}</Link>
        <Text textTransform="capitalize" color="sysTextSecondary">{`${dataType}s`}</Text>
      </div>
      <AlertCardActions alignItems="center" justifyContent="flex-end" flexWrap="wrap">
        <Radio.Group
          onChange={(e) => updateAlert({ input: { id: contentId, frequency: e.target.value } })}
          defaultValue={alertFrequency}
        >
          <Radio value="DAILY">Daily</Radio>
          <Radio value="WEEKLY">Weekly</Radio>
        </Radio.Group>
        <Modal
          open={deleteAlertOpen}
          onOpenChange={setDeleteAlertOpen}
          showCloseButton={false}
          padding={24}
        >
          <DialogWrapper column gap={16}>
            <Flex column gap={8}>
              <Text h2>Delete alert</Text>
              <Text color="sysTextSecondary">Are you sure you want to delete this alert?</Text>
            </Flex>
            <Flex justifyContent="flex-end" gap={8}>
              <Button buttonType="secondary" onClick={() => setDeleteAlertOpen(false)}>
                Cancel
              </Button>
              <Button
                buttonStyle="destructive"
                loading={isUpdatingAlert}
                onClick={() =>
                  updateAlert({
                    input: { id: contentId, frequency: LeadSubscriptionContentFrequency.None },
                  })
                }
              >
                Delete
              </Button>
            </Flex>
          </DialogWrapper>
        </Modal>
        <Button
          buttonStyle="destructive"
          buttonType="tertiary"
          icon="trash02"
          onClick={() => setDeleteAlertOpen(true)}
          aria-label="Delete alert"
        />
      </AlertCardActions>
    </AlertCard>
  );
}

const Card = styled(Flex)(({ theme }) => ({
  padding: 24,
  borderRadius: 4,
  border: `1px solid ${theme.colors.sysBorderDecorative}`,
}));
const AlertCard = styled(Flex)(({ theme }) => ({
  padding: "12px 24px",
  borderRadius: 4,
  border: `1px solid ${theme.colors.sysBorderDecorative}`,
  "@media (max-width: 900px)": {
    flexDirection: "column",
  },
}));

const AlertCardActions = styled(Flex)({
  columnGap: 48,
  flexShrink: 0,
});

const DialogWrapper = styled(Flex)({
  width: 480,
});
